import { datadogRum } from '@datadog/browser-rum';
import countries from '@trm/shared/data/countries';
import { Form, Formik, FormikHelpers } from 'formik';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as Yup from 'yup';
import FormCountrySelect from '~/components/FormCountrySelect/FormCountrySelect';
import FormRegionSelect from '~/components/FormRegionSelect/FormRegionSelect';
import {
  AddressValidationErrorModal,
  SuggestedAddressModal,
} from '~/components/modals/GoogleAddressValidation';
import { RedirectingModal } from '~/components/modals/LegacyRedirect/LegacyRedirectModal';
import { BASE_PATH } from '~/config';
import {
  AddressProps,
  AddressVerificationStatus,
  ErrorDetails,
  UNSUPPORTED_COUNTRY_ERROR_KEY,
  ValidationResponse,
  mapPostalAddressToFormik,
  useAddressValidation,
} from '~/hooks/useGoogleAddressValidation';
import { useLegacyRedirect } from '~/hooks/useLegacyRedirect';
import useRecaptchaValidation from '~/hooks/useRecaptchaValidation';
import axios from '~/utils/axios';
import { trackAccountCreation } from '~/utils/gtmUtils';
import { setOnboardingSession } from '~/utils/onboarding';

import { OnboardingButton } from '../OnboardingButton/OnboardingButton';
import { OnboardingInput } from '../OnboardingInput/OnboardingInput';

interface SocialRegistrationToken {
  cognitoSub: string;
  email: string;
  type: 'social_registration';
}

interface ContactInfoFormProps {
  email: string;
  firstName: string;
  lastName: string;
  username: string;
  street: string;
  city: string;
  region: string;
  zip: string;
  country: string;
  currencyCode: string;
  submit: string | null;
  addressVerification: AddressVerificationStatus | undefined;
}

const validationSchema = Yup.object().shape({
  firstName: Yup.string().required('First Name is required'),
  lastName: Yup.string().required('Last Name is required'),
  username: Yup.string()
    .matches(/^[a-zA-Z0-9]+$/, 'Username can only contain letters and numbers')
    .min(4, 'Username is too short')
    .required('Username is required'),
  street: Yup.string().min(4, 'Address too short.').required('Address is required'),
  city: Yup.string().required('City is required'),
  country: Yup.string().max(2, 'Use 2 Character Country Code').required('Country Code is required'),
  region: Yup.string().when('country', {
    is: (country: string) => {
      return !countries.find((c) => c.code === country)?.noRegions;
    },
    then: (schema) => schema.required('State / Province is required'),
    otherwise: (schema) => schema,
  }),
  zip: Yup.string().when('country', {
    is: (val: string) => val && val.toUpperCase() === 'US',
    then: (schema) =>
      schema.required('Must enter Zip Code').matches(/^\d+$/, 'Zip code must contain only numbers'),
  }),
});

/**
 * Contact collection component for users who signed up with social login (Google, Facebook, Apple).
 *
 * This component collects additional required information that wasn't provided by the social provider:
 * - Username (screen name)
 * - Physical address (street, city, state, zip, country)
 * - Currency preference
 *
 * The component expects a `registrationToken` query parameter containing:
 * - cognitoSub: The Cognito user ID
 * - email: Pre-filled from social provider
 * - givenName: Pre-filled first name
 * - familyName: Pre-filled last name
 *
 * @see completeSocialRegistration API endpoint
 */
const SetCognitoContactInfo = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const registrationToken = searchParams.get('registrationToken');

  const [tokenData, setTokenData] = useState<SocialRegistrationToken | null>(null);
  const [tokenError, setTokenError] = useState<string | null>(null);

  const { validateAddress, error, isValidating } = useAddressValidation();
  const [showSuggestedModal, setShowSuggestedModal] = useState(false);
  const [addressValidationResult, setAddressValidationResult] = useState<ValidationResponse | null>(
    null,
  );
  const [addressInput, setAddressInput] = useState<AddressProps | null>(null);
  const [showAddressValidationErrorModal, setShowAddressValidationErrorModal] = useState(false);
  const [validationAddressErrors, setValidationAddressErrors] = useState<
    ErrorDetails[] | undefined
  >([]);
  const [useEnteredAddress, setUseEnteredAddress] = useState(false);
  const { getToken, isReady } = useRecaptchaValidation(process.env.RECAPTCHA_SITE_KEY!);
  const { showLegacyModal, triggerLegacyRedirect } = useLegacyRedirect();

  // Decode the registration token on mount
  useEffect(() => {
    if (!registrationToken) {
      setTokenError('No registration token provided');
      return;
    }

    try {
      // Simple JWT decode (we validate signature on backend)
      const parts = registrationToken.split('.');
      if (parts.length !== 3) {
        throw new Error('Invalid token format');
      }
      const payload = JSON.parse(atob(parts[1]));

      if (payload.type !== 'social_registration') {
        throw new Error('Invalid token type');
      }

      setTokenData(payload);
    } catch (err) {
      console.error('Failed to decode registration token:', err);
      setTokenError('Invalid or expired registration link');
    }
  }, [registrationToken]);

  const scrollAndFocus = (name: string) => {
    const input = document.querySelector(`input[name="${name}"]`);
    if (input) {
      const top = input.getBoundingClientRect().top;
      window.scrollTo({ top, behavior: 'smooth' });
      (input as HTMLInputElement).focus({ preventScroll: true });
    }
  };

  const handleOnSubmit = async (
    values: ContactInfoFormProps,
    { setErrors, setStatus, setSubmitting }: FormikHelpers<ContactInfoFormProps>,
  ) => {
    try {
      if (!isReady) {
        setErrors({
          submit: 'Security check not ready. Please wait a moment and try again.',
        });
        setSubmitting(false);
        return;
      }

      const recaptchaToken = await getToken('signup');

      if (!recaptchaToken) {
        setErrors({
          submit: 'Security check not ready. Please wait a moment and try again.',
        });
        setSubmitting(false);
        return;
      }

      const addressInput = {
        regionCode: values.country,
        locality: values.city,
        administrativeArea: values.region,
        postalCode: values.zip,
        addressLines: [values.street],
      };
      setAddressInput(addressInput);

      let addressVerificationStatus = values?.addressVerification;
      try {
        const { resultAddressValidation, validation } = await validateAddress({
          address: addressInput,
        });

        if (validation?.status === UNSUPPORTED_COUNTRY_ERROR_KEY) {
          addressVerificationStatus = 'unsupported';
        }
        if (error && !isValidating) {
          setErrors({ submit: 'Something went wrong validating your address' });
          setSubmitting(false);
          return;
        }

        if (!validation.isValid && !useEnteredAddress) {
          if (validation.shouldSuggestModal) {
            setAddressValidationResult(resultAddressValidation || null);
            setShowSuggestedModal(true);
          } else {
            setShowAddressValidationErrorModal(true);
            setValidationAddressErrors(validation.errorDetails);
          }
          setSubmitting(false);
          return;
        }

        if (!useEnteredAddress) {
          addressVerificationStatus = 'verified';
        }
      } catch (err) {
        setErrors({ submit: 'Something went wrong validating your address.' });
        setSubmitting(false);
        return;
      }

      // Call complete-social-registration endpoint
      const response = await axios.post('/auth/complete-social-registration', {
        registrationToken,
        contactInfo: {
          firstName: values.firstName,
          lastName: values.lastName,
          screenName: values.username,
          street: values.street,
          city: values.city,
          region: values.region,
          zip: values.zip,
          country: values.country,
          currencyCode: values.currencyCode,
          addressVerification: addressVerificationStatus,
        },
        recaptchaToken,
      });

      setStatus({ success: true });
      trackAccountCreation('social');

      // Store email and name in sessionStorage for onboarding
      const fullName = `${values.firstName} ${values.lastName}`.trim();
      setOnboardingSession(values.email, fullName);

      // Redirect through cognito-landing to properly establish the auth session.
      // This sets the Authorization header, localStorage flags (hasLoggedInViaCognito),
      // calls /me/set-last-ip, and redirects to the dashboard.
      // A direct navigate() won't work because the React auth context state hasn't
      // been updated yet - only the axios header was set - so auth guards would
      // redirect back to login/register.
      if (response.data?.token) {
        window.location.href = `${BASE_PATH}/cognito-landing?token=${response.data.token}`;
      } else {
        navigate('/login', { replace: true });
      }
    } catch (err: unknown) {
      setStatus({ success: false });
      const errorMessage =
        (err as { response?: { data?: { message?: string } }; message?: string })?.response?.data
          ?.message || (err instanceof Error ? err.message : 'An unexpected error occurred');
      switch (errorMessage) {
        case 'Screen name already taken':
          setErrors({ username: 'This username is already taken.' });
          scrollAndFocus('username');
          break;

        case 'legacy-email':
          triggerLegacyRedirect();
          return;

        case 'Registration link expired or invalid. Please try signing up again.':
          setErrors({ submit: errorMessage });
          // Redirect to registration after 3 seconds
          setTimeout(() => navigate('/register'), 3000);
          break;

        default:
          const reportError =
            err instanceof Error ? err : new Error(`Registration failed: ${errorMessage}`);
          datadogRum.addError(reportError, {
            message: errorMessage,
            errorType: 'auth_error',
            operation: 'social_registration_complete',
            context: 'registration',
          });
          setErrors({ submit: errorMessage });
          break;
      }
      setSubmitting(false);
    }
  };

  // Show error if token is invalid
  if (tokenError || !tokenData) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-brand-primary">
        <div className="w-full max-w-[480px] border border-primary bg-primary px-4 py-12 text-center sm:p-12">
          <h2 className="mb-2 text-xl font-bold text-white">Registration Link Invalid</h2>
          <p className="mb-4 text-gray-400">
            {tokenError || 'This registration link is invalid or has expired.'}
          </p>
          <OnboardingButton onClick={() => navigate('/register')}>
            Return to Sign Up
          </OnboardingButton>
        </div>
      </div>
    );
  }

  const initialValues: ContactInfoFormProps = {
    email: tokenData.email,
    firstName: '',
    lastName: '',
    username: '',
    street: '',
    city: '',
    region: '',
    zip: '',
    country: '',
    currencyCode: '',
    submit: null,
    addressVerification: undefined,
  };

  return (
    <div className="flex min-h-screen w-full justify-center bg-gradient-brand-primary sm:items-center">
      <Formik<ContactInfoFormProps>
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleOnSubmit}
        validateOnChange
        validateOnBlur
      >
        {({ errors, handleBlur, handleSubmit, isSubmitting, setFieldValue, touched, values }) => {
          const isStateDisabled =
            values.country === '' ||
            countries.find((country) => country.code === values.country)?.noRegions;

          const hasUserFacingErrors =
            Object.keys(errors).filter((k) => {
              if (['submit', 'addressVerification'].includes(k)) return false;
              return touched[k as keyof typeof touched];
            }).length > 0;

          const confirmAddress = (addressVerificationStatus: AddressVerificationStatus) => {
            setUseEnteredAddress(true);
            setShowSuggestedModal(false);
            setShowAddressValidationErrorModal(false);
            setFieldValue('addressVerification', addressVerificationStatus);
            handleSubmit();
          };

          const handleFieldChange = (field: string, value: string) => {
            setUseEnteredAddress(false);
            setFieldValue(field, value);
          };

          const isUSOrCA = values.country === 'US' || values.country === 'CA';
          const isUS = values.country === 'US';
          const regionLabel = isUS ? 'State' : 'Province';
          const regionPlaceholder = isUS ? 'Select state...' : 'Select province...';

          return (
            <Form className="w-full max-w-160 px-4 py-12 sm:border sm:border-primary sm:bg-primary sm:p-12">
              {/* Header */}
              <div className="mb-6 text-center sm:text-start">
                <h1 className="mb-2 text-2xl font-bold text-white">Set up your trader account</h1>
              </div>
              <div className="mb-6 border-t border-gray-700 pt-6">
                <h3 className="mb-1 text-base font-semibold text-white">Tell us about yourself</h3>
                <p className="mb-4 text-sm text-gray-400">
                  Use the legal name on your IDs (i.e., Driver's License, Passport).
                </p>
              </div>

              <div className="space-y-6">
                {/* Email (read-only)
                <Input
                  type="email"
                  name="email"
                  label="Email"
                  value={values.email}
                  onChange={() => {}}
                  isDisabled={true}
                  size="md"
                  wrapperClassName="!rounded-none"
                  inputClassName="!rounded-none bg-gray-50"
                /> */}

                {/* First/Last Name */}
                <div className="grid grid-cols-2 gap-6">
                  <OnboardingInput
                    id="firstName-input"
                    type="text"
                    name="firstName"
                    label="Legal First Name"
                    value={values.firstName}
                    onChange={(value) => setFieldValue('firstName', value)}
                    onBlur={handleBlur}
                    isDisabled={isSubmitting}
                    isInvalid={!!(errors.firstName && touched.firstName)}
                    error={errors.firstName && touched.firstName ? errors.firstName : undefined}
                    size="md"
                  />
                  <OnboardingInput
                    id="lastName-input"
                    type="text"
                    name="lastName"
                    label="Legal Last Name"
                    value={values.lastName}
                    onChange={(value) => setFieldValue('lastName', value)}
                    onBlur={handleBlur}
                    isDisabled={isSubmitting}
                    isInvalid={!!(errors.lastName && touched.lastName)}
                    error={errors.lastName && touched.lastName ? errors.lastName : undefined}
                    size="md"
                  />
                </div>

                {/* Username */}
                <OnboardingInput
                  type="text"
                  id="username-input"
                  name="username"
                  label="Username"
                  placeholder="Choose Username"
                  value={values.username}
                  onChange={(value) => setFieldValue('username', value)}
                  onBlur={handleBlur}
                  isDisabled={isSubmitting}
                  isInvalid={!!(errors.username && touched.username)}
                  error={errors.username && touched.username ? errors.username : undefined}
                  size="md"
                />

                {/* Country */}
                <FormCountrySelect
                  label="Country"
                  name="country"
                  placeholder="Select country"
                  value={values.country}
                  onChange={handleFieldChange}
                  onBlur={handleBlur}
                  error={errors.country}
                  touched={touched.country}
                  disabled={isSubmitting}
                />

                {/* Street Address */}
                <OnboardingInput
                  id="street-input"
                  type="text"
                  name="street"
                  label="Street Address"
                  value={values.street}
                  onChange={(value) => handleFieldChange('street', value)}
                  onBlur={handleBlur}
                  isDisabled={isSubmitting}
                  isInvalid={!!(errors.street && touched.street)}
                  error={errors.street && touched.street ? errors.street : undefined}
                  size="md"
                />

                {/* City, Zip Code, State */}
                <div className="grid grid-cols-2 gap-6 sm:grid-cols-3">
                  <OnboardingInput
                    id="city-input"
                    type="text"
                    name="city"
                    label="City"
                    value={values.city}
                    onChange={(value) => handleFieldChange('city', value)}
                    onBlur={handleBlur}
                    isDisabled={isSubmitting}
                    isInvalid={!!(errors.city && touched.city)}
                    error={errors.city && touched.city ? errors.city : undefined}
                    size="md"
                  />
                  <OnboardingInput
                    id="zip-input"
                    type="text"
                    name="zip"
                    label="Zip Code"
                    value={values.zip}
                    onChange={(value) => handleFieldChange('zip', value)}
                    onBlur={handleBlur}
                    isDisabled={isSubmitting}
                    isInvalid={!!(errors.zip && touched.zip)}
                    error={errors.zip && touched.zip ? errors.zip : undefined}
                    size="md"
                  />
                  <div className="col-span-2 sm:col-span-1">
                    {isUSOrCA ? (
                      <FormRegionSelect
                        label={regionLabel}
                        country={values.country}
                        name="region"
                        placeholder={regionPlaceholder}
                        value={values.region}
                        onChange={handleFieldChange}
                        onBlur={handleBlur}
                        error={errors.region}
                        touched={touched.region}
                        disabled={isSubmitting || isStateDisabled}
                      />
                    ) : (
                      <OnboardingInput
                        id="region-input"
                        type="text"
                        name="region"
                        label="State / Region"
                        placeholder="Enter state / region"
                        value={values.region}
                        onChange={(value) => handleFieldChange('region', value)}
                        onBlur={handleBlur}
                        isDisabled={isSubmitting || isStateDisabled}
                        isInvalid={!!(errors.region && touched.region)}
                        error={errors.region && touched.region ? errors.region : undefined}
                        size="md"
                      />
                    )}
                  </div>
                </div>

                {errors.submit && (
                  <div className="rounded border border-red-400 bg-red-900/20 p-4">
                    <p className="text-sm text-red-400">{errors.submit}</p>
                  </div>
                )}

                <OnboardingButton
                  type="submit"
                  isDisabled={isSubmitting || hasUserFacingErrors}
                  isLoading={isSubmitting}
                  className="mt-4"
                >
                  Create Account
                </OnboardingButton>
              </div>

              {/* Modals */}
              <SuggestedAddressModal
                open={showSuggestedModal}
                suggestedAddress={addressValidationResult?.result?.address?.postalAddress || null}
                longitude={addressValidationResult?.result?.geocode?.location.longitude}
                latitude={addressValidationResult?.result?.geocode?.location.latitude}
                userAddress={addressInput}
                onConfirm={(
                  selected: AddressProps | null,
                  selectedOption: 'user' | 'suggested',
                ) => {
                  if (selected) {
                    mapPostalAddressToFormik(selected, setFieldValue, addressInput);
                    const addressVerificationStatus =
                      selectedOption === 'suggested' ? 'verified' : 'overridden';
                    confirmAddress(addressVerificationStatus);
                  }
                }}
                onClose={() => {
                  setShowSuggestedModal(false);
                }}
              />
              <AddressValidationErrorModal
                userAddress={addressInput}
                open={showAddressValidationErrorModal}
                errors={validationAddressErrors || []}
                onUseCurrent={() => {
                  confirmAddress('overridden');
                }}
                onClose={() => {
                  setUseEnteredAddress(false);
                  setShowAddressValidationErrorModal(false);
                }}
              />
              {showLegacyModal && (
                <RedirectingModal
                  open={showLegacyModal}
                  title="You're already registered!"
                  messageLines={[
                    'It looks like this email is already associated with a Topstep account.',
                    "We're redirecting you to the correct place...",
                  ]}
                />
              )}
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default SetCognitoContactInfo;
